import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { userPages } from "../lib/pages";
import { ID_KINDS, dmy, recName } from "../lib/identity";
import { useIdTemple, TemplePicker, useIdMasters, PageHead, StatusChip, ValidityChip, PhotoField, VerifyDialog, fetchAndPrint, EventNameEditor, CardPrintSettings } from "../components/IdentityKit";
import { QrScanner } from "../components/QrScanButton";
import DirectPrintBanner from "../components/DirectPrintBanner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Printer, ScanLine, Search, X, Camera, Pencil } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const plusDays = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };

const SUB = {
  duty: "Officers and staff on duty get a 4 x 6 printable ID card after approval.",
  vehicle: "A4 landscape vehicle pass for the windscreen, with the department shown big in its colour.",
  children: "QR safety tags help reunite missing children with their families.",
  pandit: "Live-photo registration with a 4 x 6 printable Seva Card.",
};
const EYEBROW = { duty: "Identity Cards", vehicle: "Vehicle Permits", children: "Child Safety", pandit: "Sacred Officiants" };

function blank(kind) {
  const f = Object.fromEntries(ID_KINDS[kind].fields.map((x) => [x.key, ""]));
  if (kind === "pandit") f.state = "Telangana";
  return { ...f, photo: "", valid_from: today(), valid_to: plusDays(today(), 10) };
}

function PassForm({ kind, templeId, onSaved, onCancel, edit, initialTag }) {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role);
  const k = ID_KINDS[kind];
  const { departments, locations } = useIdMasters(templeId);
  // edit: a full record (Super Admin correcting a pass) - the form starts with its values
  const [f, setF] = useState(() => (edit ? { ...blank(kind), ...Object.fromEntries(Object.entries(edit).filter(([key]) => key in blank(kind))) } : { ...blank(kind), ...(initialTag ? { tag_serial: initialTag } : {}) }));
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);
  const set = (key, v) => setF((x) => ({ ...x, [key]: v }));
  const days = f.valid_from && f.valid_to ? Math.round((new Date(f.valid_to) - new Date(f.valid_from)) / 86400000) + 1 : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (days <= 0) { toast.error("Valid To must be on or after Valid From"); return; }
    setBusy(true);
    try {
      if (edit) {
        await api.put(`/identity/${kind}/${edit.id}`, f);
        toast.success(`${kind === "children" ? edit.tag_serial : edit.pass_no} updated (audited)`);
        onSaved({ ...edit, status: edit.status, edited: true });
        return;
      }
      const r = await api.post(`/identity/${kind}`, { ...f, temple_id: templeId });
      toast.success(`${k.label} ${kind === "children" ? r.data.tag_serial : r.data.pass_no} saved${r.data.status === "PENDING" ? " - sent for approval" : ""}`);
      onSaved(r.data);
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const input = (x) => {
    const common = { value: f[x.key] ?? "", onChange: (e) => set(x.key, x.upper ? e.target.value.toUpperCase() : e.target.value), placeholder: x.placeholder || "", "data-testid": `id-f-${x.key}` };
    if (x.type === "department" || x.type === "location") {
      const list = x.type === "department" ? departments : locations.filter((l) => !l.hidden);
      return (
        <select {...common} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
          <option value="">{list.length ? `Select ${x.type === "department" ? "department" : "ghat / location"}` : `No ${x.type === "department" ? "departments" : "locations"} yet - add them in Masters`}</option>
          {list.map((o) => <option key={o.id} value={o.id}>{o.name}{o.kind && x.type === "location" ? ` (${o.kind[0] + o.kind.slice(1).toLowerCase()})` : ""}</option>)}
        </select>
      );
    }
    if (x.type === "select") return (
      <select {...common} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
        <option value="">Select</option>{x.options.map((o) => <option key={o}>{o}</option>)}
      </select>
    );
    if (x.type === "tag") return (
      <div className="flex gap-2">
        <Input {...common} className="font-mono" placeholder={isAdmin ? "e.g. ssd0000001 (blank = next free tag)" : "Scan or type the tag, e.g. ssd0000001"} disabled={!!edit} />
        {!edit && <Button type="button" variant="outline" onClick={() => setScan(true)} data-testid="id-tag-scan"><Camera className="w-4 h-4 mr-1" /> Scan tag</Button>}
      </div>
    );
    return <Input {...common} type={x.type === "number" ? "number" : x.type === "mobile" ? "tel" : "text"} inputMode={x.type === "mobile" ? "numeric" : undefined} />;
  };

  return (
    <Card className="mb-4 border-2 border-[#F2DCCB]" data-testid="id-form">
      <CardContent className="p-4">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            {k.fields.map((x) => (
              <div key={x.key} className={x.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{x.label}{x.required && " *"}</Label>{input(x)}
              </div>
            ))}
          </div>
          <div className="rounded-lg bg-[#FFF7F0] border border-[#F6D5BC] p-3 grid sm:grid-cols-3 gap-3 items-end">
            <div><Label className="text-xs">Valid From *</Label><Input type="date" value={f.valid_from} onChange={(e) => set("valid_from", e.target.value)} data-testid="id-valid-from" /></div>
            <div><Label className="text-xs">Valid To *</Label><Input type="date" min={f.valid_from} value={f.valid_to} onChange={(e) => set("valid_to", e.target.value)} data-testid="id-valid-to" /></div>
            <p className="text-sm font-semibold text-[#9A3412]">Validity period: {days > 0 ? `${days} day${days > 1 ? "s" : ""}` : "-"}</p>
          </div>
          {k.photo && <PhotoField value={f.photo} onChange={(v) => set("photo", v)} label={kind === "pandit" ? "Photo (live capture)" : "Photo"} />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-submit">
              {edit ? "Save changes" : kind === "children" ? "Assign Tag" : k.approval ? "Submit" : "Save"}
            </Button>
          </div>
        </form>
      </CardContent>
      <Dialog open={scan} onOpenChange={setScan}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Scan Tag QR Code</DialogTitle></DialogHeader>
          {scan && <QrScanner className="rounded-lg min-h-[240px]" onScan={(t) => { set("tag_serial", (t.split("|")[1] || t).trim().toLowerCase()); setScan(false); }} onError={() => setScan(false)} />}
          <p className="text-xs text-slate-500 text-center">Point the camera at the pre-printed QR on the child's safety tag, or type the serial in the form.</p>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function IdentityPasses({ kind }) {
  const k = ID_KINDS[kind];
  const { user } = useAuth();
  const pages = userPages(user);
  const { temples, templeId, setTempleId, reload } = useIdTemple(user);
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const [show, setShow] = useState(params.get("do") === "assign");
  const [initialTag] = useState(params.get("tag") || "");
  const [editing, setEditing] = useState(null); // full record being corrected (Super Admin only)
  const isSA = user.role === "SUPER_ADMIN";
  const startEdit = async (r) => {
    try {
      const full = await api.get(`/identity/${kind}/${r.id}`);
      setShow(false); setEditing(full.data); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const [verify, setVerify] = useState(params.get("do") === "verify");
  const [qtext, setQtext] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    if (!templeId) return;
    api.get(`/identity/${kind}`, { params: { temple_id: templeId, status } }).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [kind, templeId, status]);
  useEffect(() => { load(); }, [load]);
  // Mobile app buttons open straight into Assign Tag / Verify Tag (?do=assign / ?do=verify)
  useEffect(() => {
    const d = params.get("do");
    if (d) { if (d === "assign") setShow(true); if (d === "verify") setVerify(true); setParams({}, { replace: true }); }
  }, []); // eslint-disable-line

  const shown = useMemo(() => {
    const s = qtext.trim().toLowerCase();
    return !s ? rows : rows.filter((r) => [r.pass_no, r.tag_serial, recName(r), r.mobile, r.vehicle_number, r.father_name].some((v) => String(v || "").toLowerCase().includes(s)));
  }, [rows, qtext]);

  return (
    <div data-testid={`id-page-${kind}`}>
      <PageHead eyebrow={EYEBROW[kind]} title={kind === "pandit" ? "Pandit Registration" : kind === "duty" ? "Duty Pass Registration" : k.label} subtitle={SUB[kind]}>
        <TemplePicker user={user} temples={temples} value={templeId} onChange={setTempleId} />
        {kind !== "vehicle" && <CardPrintSettings temple={temples.find((t) => t.temple_id === templeId)} />}
        {kind === "children" && <Button variant="outline" onClick={() => setVerify(true)} className="border-sky-600 text-sky-700" data-testid="id-verify-btn"><ScanLine className="w-4 h-4 mr-1" /> Verify Tag</Button>}
        {pages.includes(k.page) && (
          <Button onClick={() => setShow((s) => !s)} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-new">
            {show ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}{show ? "Close" : kind === "children" ? "Assign Tag" : kind === "pandit" ? "Register Pandit" : `New ${k.label}`}
          </Button>
        )}
      </PageHead>

      <DirectPrintBanner templeId={templeId} className="mb-3" only={[kind === "vehicle" ? "vehicle" : "idcard"]} />
      {pages.includes("ID_MASTERS") && <div className="mb-3"><EventNameEditor templeId={templeId} temples={temples} onSaved={reload} /></div>}

      {editing && (
        <div className="mb-1">
          <p className="text-sm font-semibold text-[#9A3412] mb-1">Editing {kind === "children" ? editing.tag_serial : editing.pass_no} · {recName(editing)}</p>
          <PassForm key={editing.id} kind={kind} templeId={editing.temple_id} edit={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
      )}
      {show && templeId && <PassForm key={templeId} initialTag={initialTag} kind={kind} templeId={templeId} onCancel={() => setShow(false)} onSaved={(r) => { setShow(false); load(); if (r.status !== "PENDING") fetchAndPrint(kind, [r], temples); }} />}

      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={qtext} onChange={(e) => setQtext(e.target.value)} placeholder="Search number, name, mobile…" className="pl-9" data-testid="id-search" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid="id-status-filter">
              <option value="">All status</option>
              {(kind === "children" ? ["ISSUED", "RETURNED"] : ["PENDING", "APPROVED", "REJECTED"]).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="id-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                <th className="py-2 pr-3">{kind === "children" ? "Tag ID" : "Pass No"}</th>
                {k.columns.filter(([c]) => c !== "tag_serial").map(([, l]) => <th key={l} className="py-2 pr-3">{l}</th>)}
                <th className="py-2 pr-3">Validity</th><th className="py-2 pr-3">Status</th><th className="py-2 text-right">Action</th>
              </tr></thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={k.columns.length + 4} className="py-10 text-center text-slate-400">No records yet</td></tr>}
                {shown.map((r) => (
                  <tr key={r.id} className="border-b border-[#F1EBE3] hover:bg-[#FFFAF5]">
                    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{kind === "children" ? r.tag_serial : r.pass_no}</td>
                    {k.columns.filter(([c]) => c !== "tag_serial").map(([c]) => (
                      <td key={c} className={`py-2 pr-3 ${c === "vehicle_number" || c === "child_name" ? "font-bold font-mono" : ""} ${c === "child_name" ? "font-sans" : ""}`}>
                        {c === "department_name" && r.department_color ? <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: r.department_color }} />{r[c]}</span> : (r[c] ?? "-") || "-"}
                      </td>
                    ))}
                    <td className="py-2 pr-3 whitespace-nowrap"><span className="text-xs text-slate-600">{dmy(r.valid_from)} – {dmy(r.valid_to)}</span><br /><ValidityChip v={r.validity} /></td>
                    <td className="py-2 pr-3"><StatusChip s={r.status} />{r.remarks && <span className="block text-[11px] text-red-600 max-w-[180px]">{r.remarks}</span>}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {isSA && <Button size="sm" variant="ghost" title="Edit (Super Admin)" onClick={() => startEdit(r)} data-testid={`id-edit-${r.pass_no}`}><Pencil className="w-4 h-4 text-saffron-600" /></Button>}
                      <Button size="sm" variant="ghost" title={r.status === "PENDING" ? "Print after approval" : "Print"} disabled={r.status === "PENDING" || r.status === "REJECTED"}
                        onClick={() => fetchAndPrint(kind, [r], temples)} data-testid={`id-print-${r.pass_no}`}><Printer className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length > 0 && <p className="text-xs text-slate-500 mt-2">{shown.length} record{shown.length > 1 ? "s" : ""}</p>}
        </CardContent>
      </Card>
      <VerifyDialog open={verify} onClose={() => setVerify(false)} temples={temples} title="Verify Children Tag" />
    </div>
  );
}
